import React, { useEffect, useRef } from 'react'
import styles from '@shared/styles/components/NoticeBar.module.css'
import { SystemIcon } from '@shared/designSystem/SystemIcon'
import type { NoticeLevel } from '@shared/designSystem/NoticeBanner'

interface NoticeBarProps {
    ariaLabel: string
    /** Drives the 0 ↔ content height transition. */
    hasContent: boolean
    children?: React.ReactNode
    /** Popover rendered anchored under the bar (NoticePanel). */
    panel?: React.ReactNode
    className?: string
}

/**
 * Shell region that hosts NoticeBanner rows above the page. It lives in the
 * layout flow (its height is real), grows from 0 when the first notice lands
 * and overlays its popover on the page instead of pushing it.
 */
export const NoticeBar = React.forwardRef<HTMLElement, NoticeBarProps>(({ ariaLabel, hasContent, children, panel, className }, ref) => (
    <section
        ref={ref}
        aria-label={ariaLabel}
        className={[styles.bar, hasContent ? styles.open : '', 'DSNoticeBar', className].filter(Boolean).join(' ')}
    >
        <div className={styles.rows}>{children}</div>
        {panel}
    </section>
))
NoticeBar.displayName = 'NoticeBar'

interface NoticeCountPillProps {
    label: string
    expanded: boolean
    onClick: () => void
    /** Change it to replay the one-shot pulse (a deferred notice arrived). */
    pulseKey?: string | number
}

export const NoticeCountPill = React.forwardRef<HTMLButtonElement, NoticeCountPillProps>(({ label, expanded, onClick, pulseKey }, ref) => (
    <button
        ref={ref}
        key={pulseKey}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={expanded}
        onClick={onClick}
        className={[styles.pill, pulseKey !== undefined ? styles.pulse : ''].filter(Boolean).join(' ')}
    >
        {label}
    </button>
))
NoticeCountPill.displayName = 'NoticeCountPill'

interface NoticePanelProps {
    title: string
    closeLabel: string
    onClose: () => void
    children: React.ReactNode
    /** Full-width sheet under the bar (narrow panes). */
    sheet?: boolean
    /** Receives focus back when the panel closes (the pill). */
    returnFocusTo?: React.RefObject<HTMLElement>
}

export const NoticePanel: React.FC<NoticePanelProps> = ({ title, closeLabel, onClose, children, sheet = false, returnFocusTo }) => {
    const panelRef = useRef<HTMLDivElement>(null)
    const headingRef = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
        headingRef.current?.focus()
        const returnTarget = returnFocusTo?.current
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
            }
        }
        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node
            if (panelRef.current?.contains(target) || returnFocusTo?.current?.contains(target)) {
                return
            }
            onClose()
        }
        window.addEventListener('keydown', handleKeyDown)
        document.addEventListener('mousedown', handlePointerDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('mousedown', handlePointerDown)
            returnTarget?.focus?.()
        }
    }, [onClose, returnFocusTo])

    return (
        <div ref={panelRef} role="dialog" aria-label={title} className={[styles.panel, sheet ? styles.sheet : ''].filter(Boolean).join(' ')}>
            <div className={styles.panelHeader}>
                <h2 ref={headingRef} tabIndex={-1} className={styles.panelTitle}>{title}</h2>
                <button type="button" className={styles.panelClose} aria-label={closeLabel} title={closeLabel} onClick={onClose}>
                    <SystemIcon type="close" color="currentColor" />
                </button>
            </div>
            <div className={styles.panelList}>{children}</div>
        </div>
    )
}

interface NoticeSummaryRowProps {
    level: NoticeLevel
    levelLabel: string
    title: string
    countLabel: string
    expanded: boolean
    onClick: () => void
}

const SUMMARY_ICON = { critical: 'warning-triangle', warning: 'clock', communication: 'info' } as const

/** Narrow-pane fallback: one tappable row with the top notice and a total count. */
export const NoticeSummaryRow = React.forwardRef<HTMLButtonElement, NoticeSummaryRowProps>(({ level, levelLabel, title, countLabel, expanded, onClick }, ref) => (
    <button
        ref={ref}
        type="button"
        data-level={level}
        aria-haspopup="dialog"
        aria-expanded={expanded}
        onClick={onClick}
        className={styles.summary}
    >
        <span className={styles.summaryIcon} aria-hidden="true">
            <SystemIcon type={SUMMARY_ICON[level]} color="currentColor" />
        </span>
        <span className={styles.summaryTitle}>
            <span className={styles.srOnly}>{levelLabel}: </span>
            {title}
        </span>
        {countLabel ? <span className={styles.pill}>{countLabel}</span> : null}
    </button>
))
NoticeSummaryRow.displayName = 'NoticeSummaryRow'

/** Polite live region for announcements that are not rows (e.g. "1 aviso nuevo"). */
export const NoticeAnnouncer: React.FC<{ message: string }> = ({ message }) => (
    <span className={styles.srOnly} aria-live="polite">{message}</span>
)
